import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { Breadcrumb, IBreadcrumbItem, mergeStyleSets, Spinner, SpinnerSize, Text } from '@fluentui/react';
import API, { EntityAPI, EntityType, IEntityContentItem, IGenericEntity, IUserConfigPackage } from '../../API';
import LegalEntityDocument from '../../outlook/LegalEntityDocument';
import DocumentViewerDialog, { DocumentViewerDialogState } from './documents/DocumentViewerDialog';
import SearchCtrl, { SearchCtrlStyle } from './SearchCtrl';
import DocumentSearchResults, { DocumentSearchResultsState } from './documents/DocumentSearchResults';

export class EntityFileBrowserState {
	public userConfig: IUserConfigPackage;
	public entity: IGenericEntity;
	public entityType: string | EntityType;

	public loading = false;
	public folders: IEntityContentItem[] = [];
	public documents: IEntityContentItem[] = [];
	public breadcrumb: { name: string; url: string }[] = [];

	public docViewerDialog: DocumentViewerDialogState;
	public docSearchResults: DocumentSearchResultsState;
	
	public constructor(userConfig: IUserConfigPackage, entityType: string | EntityType, entity: IGenericEntity) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.entity = entity;
		this.entityType = entityType;
		this.docViewerDialog = new DocumentViewerDialogState(userConfig);
		this.docSearchResults = new DocumentSearchResultsState(userConfig, false, (item) => {
			this.docViewerDialog.show(item.webUrl, item.name);
		});
		this.docSearchResults.setScopeToPath(this.entity.ServerRelativeUrl);
	}

	public openDocument(doc: IEntityContentItem) {
		this.docViewerDialog.show(doc.webUrl, doc.name);
	}

	public ensureLoaded() {
		this.loadRoot();
	}

	public searchString: string = '';
	public setSearchString(value: string) {
		this.searchString = value;
		if(this.searchString) {
			this.docSearchResults.setSearchText(this.searchString);
		}
	}

	private async loadRoot() {
		this.loading = true;
		this.breadcrumb = [];
		try {

			//If there's a search string, then we want flat files
			let flatFiles:boolean = !!this.searchString;

			const api = await EntityAPI.fromEntityTypeOrId(this.entityType, this.userConfig);
			const result = await api.documents(this.entity.Id, flatFiles, this.searchString);
			this.folders = result.folders;
			this.documents = result.documents;
		} finally {
			this.loading = false;
		}
	}

	public async openFolder(folder: IEntityContentItem) {
		this.loading = true;
		this.breadcrumb = [...this.breadcrumb, { name: folder.name, url: folder.webUrl }];
		try {
			const token = await this.userConfig.getToken();
			const api = new API(token);
			const result = await api.documentsByFolder(folder.webUrl, false);
			this.folders = result.folders;
			this.documents = result.documents;
		} finally {
			this.loading = false;
		}
	}

	public async navigateToBreadcrumb(index: number) {
		if (index < 0) {
			this.loadRoot();
			return;
		}
		const crumb = this.breadcrumb[index];
		this.breadcrumb = this.breadcrumb.slice(0, index + 1);
		this.loading = true;
		try {
			const token = await this.userConfig.getToken();
			const api = new API(token);
			const result = await api.documentsByFolder(crumb.url, false);
			this.folders = result.folders;
			this.documents = result.documents;
		} finally {
			this.loading = false;
		}
	}
}

export interface IEntityFileBrowserProps {
	state: EntityFileBrowserState;
}

const classNames = mergeStyleSets({
	row: {
		display: 'flex',
		alignItems: 'center',
		minHeight: 44,
		padding: '0 8px',
		gap: 12,
		cursor: 'pointer',
		borderBottom: '1px solid #edebe9',
		selectors: {
			'&:hover': { background: '#f3f2f1' },
			'&:active': { background: '#edebe9' },
		},
	},
	icon: {
		width: 24,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	name: {
		flex: 1,
		fontSize: 14,
	},
	meta: {
		fontSize: 12,
		color: '#605e5c',
		whiteSpace: 'nowrap',
	},
});

class EntityFileBrowser extends React.Component<IEntityFileBrowserProps> {

	componentDidMount() {
		this.props.state.ensureLoaded();
	}

	render() {
		
		return (
			<div>
				<DocumentViewerDialog state={this.props.state.docViewerDialog} />
				<SearchCtrl
					initialValue={this.props.state.searchString}
					style={SearchCtrlStyle.Standard}
					placeholder={`Search this ${this.props.state.userConfig.entityTypes.getConfig(this.props.state.entityType)?.singularName}...`}
					onSearchTextChanged={s => this.props.state.setSearchString(s)}
				/>

				{
					this.props.state.loading ? 
						<Spinner size={SpinnerSize.medium} style={{ marginTop: 16 }} /> :
						this.getContent()
				}

			</div>
		);
	}

	private getContent() {
		
		if(this.props.state.searchString) {
			return <>
				<DocumentSearchResults state={this.props.state.docSearchResults} />
			</>;
		} else {

			const { folders, documents, breadcrumb } = this.props.state;

			let breadcrumbItems: IBreadcrumbItem[] = [
				{ text: 'Root folder', key: 'root', onClick: () => this.props.state.navigateToBreadcrumb(-1) },
				...breadcrumb.map((crumb, i) => ({
					text: crumb.name,
					key: crumb.url,
					onClick: i < breadcrumb.length - 1 ? () => this.props.state.navigateToBreadcrumb(i) : undefined,
				})),
			];

			return <>
				<Breadcrumb items={breadcrumbItems} maxDisplayedItems={5} />
				<>
					{folders.map(f => this.renderFolder(f))}
					{documents.map(d => this.renderDocument(d))}
					{folders.length === 0 && documents.length === 0 &&
						<Text variant="small" style={{ color: '#605e5c', padding: '12px 8px', display: 'block' }}>No files.</Text>
					}
				</>
			</>
		}
	}

	private renderFolder(folder: IEntityContentItem) {
		return (
			<div key={folder.id} className={classNames.row} onClick={() => this.props.state.openFolder(folder)}>
				<div className={classNames.icon}>{LegalEntityDocument.getIcon({ ...folder, type: 'folder' })}</div>
				<Text className={classNames.name}>{folder.name}</Text>
			</div>
		);
	}

	private renderDocument(doc: IEntityContentItem) {
		return (
			<div key={doc.id} className={classNames.row} onClick={() => this.props.state.openDocument(doc)}>
				<div className={classNames.icon}>{LegalEntityDocument.getIcon({ ...doc, type: 'document' })}</div>
				<Text className={classNames.name}>{doc.name}</Text>
				<Text className={classNames.meta}>
					{doc.modifiedByName && `${doc.modifiedByName} · `}
					{doc.modified && new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium' }).format(new Date(doc.modified))}
				</Text>
			</div>
		);
	}
}

export default observer(EntityFileBrowser);
